import { useApolloClient, useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { PostHogVariant } from '@eluve/api-contract';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Checkbox,
  HStack,
  NewButton,
  Text,
  VStack,
  useToast,
} from '@eluve/components';

import {
  createExperimentVariantMutation,
  deleteExperimentVariantMutation,
  getExperimentByIdQuery,
} from './operations';
import { CURRENT_CONFIG_VERSION, VersionedVariantConfig } from './overrides';

type ExistingVariant = {
  id: string;
  posthogVariantKey: string;
};

interface SyncFromPostHogDialogProps {
  experimentId: string;
  posthogFlagKey: string;
  existingVariants: ExistingVariant[];
  isActivated: boolean;
}

export const SyncFromPostHogDialog: React.FC<SyncFromPostHogDialogProps> = ({
  experimentId,
  posthogFlagKey,
  existingVariants,
  isActivated,
}) => {
  const toast = useToast();
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [variantsToAdd, setVariantsToAdd] = useState<PostHogVariant[]>([]);
  const [variantsToRemove, setVariantsToRemove] = useState<ExistingVariant[]>(
    [],
  );
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set());
  const [selectedToRemove, setSelectedToRemove] = useState<Set<string>>(
    new Set(),
  );
  const [error, setError] = useState<string | null>(null);

  const [createVariant] = useMutation(createExperimentVariantMutation);

  const [deleteVariant] = useMutation(deleteExperimentVariantMutation);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setVariantsToAdd([]);
    setVariantsToRemove([]);
    setSelectedToAdd(new Set());
    setSelectedToRemove(new Set());

    try {
      const response = await apiClient.experiments.syncFromPostHog({
        body: { posthogFlagKey },
      });

      if (response.status === 200) {
        const posthogKeys = new Set(response.body.variants.map((v) => v.key));
        const existingKeys = new Set(
          existingVariants.map((v) => v.posthogVariantKey),
        );

        const toAdd = response.body.variants.filter(
          (v) => !existingKeys.has(v.key),
        );
        const toRemove = existingVariants.filter(
          (v) => !posthogKeys.has(v.posthogVariantKey),
        );

        setVariantsToAdd(toAdd);
        setVariantsToRemove(toRemove);
        setSelectedToAdd(new Set(toAdd.map((v) => v.key)));
        setSelectedToRemove(new Set(toRemove.map((v) => v.id)));

        if (toAdd.length === 0 && toRemove.length === 0) {
          setError('Variants are already in sync with PostHog');
        }
      } else if (response.status === 404 || response.status === 500) {
        setError(response.body.error);
      }
    } catch {
      setError('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  const toggleAddVariant = (key: string) => {
    const newSelected = new Set(selectedToAdd);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedToAdd(newSelected);
  };

  const toggleRemoveVariant = (id: string) => {
    const newSelected = new Set(selectedToRemove);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedToRemove(newSelected);
  };

  const handleSync = async () => {
    if (selectedToAdd.size === 0 && selectedToRemove.size === 0) {
      toast.error('Please select at least one change to sync');
      return;
    }

    setSyncing(true);
    let addedCount = 0;
    let removedCount = 0;
    let failCount = 0;

    for (const variant of variantsToAdd) {
      if (!selectedToAdd.has(variant.key)) continue;

      try {
        const emptyVersionedConfig: VersionedVariantConfig = {
          version: CURRENT_CONFIG_VERSION,
          publishedAt: new Date().toISOString(),
          config: {},
        };
        await createVariant({
          variables: {
            experimentId,
            name: variant.key,
            posthogVariantKey: variant.key,
            variantConfig: emptyVersionedConfig,
          },
        });
        addedCount++;
      } catch {
        failCount++;
      }
    }

    for (const variant of variantsToRemove) {
      if (!selectedToRemove.has(variant.id)) continue;

      try {
        await deleteVariant({
          variables: { variantId: variant.id },
        });
        removedCount++;
      } catch {
        failCount++;
      }
    }

    // Single refetch after all mutations complete to ensure cache is updated
    await apolloClient.refetchQueries({
      include: [getExperimentByIdQuery],
    });

    setSyncing(false);
    setOpen(false);

    const messages: string[] = [];
    if (addedCount > 0) messages.push(`added ${addedCount}`);
    if (removedCount > 0) messages.push(`removed ${removedCount}`);

    if (failCount === 0) {
      toast.success(`Synced: ${messages.join(', ')}`);
    } else {
      toast.error(`${messages.join(', ')}, ${failCount} failed`);
    }
  };

  const totalSelected = selectedToAdd.size + selectedToRemove.size;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <NewButton
          type="outline"
          text="Sync from PostHog"
          onClick={handleOpen}
          disabled={isActivated || !posthogFlagKey}
        />
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sync Variants from PostHog</AlertDialogTitle>
          <AlertDialogDescription>
            Fetching variants for flag: <strong>{posthogFlagKey}</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <VStack gap={3} className="py-4">
          {loading && (
            <Text color="tertiary">Loading variants from PostHog...</Text>
          )}

          {error && <Text color="tertiary">{error}</Text>}

          {!loading &&
            !error &&
            variantsToAdd.length === 0 &&
            variantsToRemove.length === 0 && (
              <Text color="tertiary">No changes needed</Text>
            )}

          {!loading && variantsToAdd.length > 0 && (
            <VStack gap={2}>
              <Text size="sm" color="secondary" weight="medium">
                Variants to add:
              </Text>
              {variantsToAdd.map((variant) => (
                <HStack
                  key={variant.key}
                  gap={2}
                  align="center"
                  className="rounded border border-border-primary bg-bg-positive-subtle p-2"
                >
                  <Checkbox
                    checked={selectedToAdd.has(variant.key)}
                    onCheckedChange={() => toggleAddVariant(variant.key)}
                  />
                  <VStack gap={0}>
                    <Text weight="medium">{variant.key}</Text>
                    <Text size="sm" color="tertiary">
                      Key: {variant.key} ({variant.rollout_percentage}%)
                    </Text>
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}

          {!loading && variantsToRemove.length > 0 && (
            <VStack gap={2}>
              <Text size="sm" color="secondary" weight="medium">
                Variants to remove (not in PostHog):
              </Text>
              {variantsToRemove.map((variant) => (
                <HStack
                  key={variant.id}
                  gap={2}
                  align="center"
                  className="rounded border border-border-primary bg-bg-negative-subtle p-2"
                >
                  <Checkbox
                    checked={selectedToRemove.has(variant.id)}
                    onCheckedChange={() => toggleRemoveVariant(variant.id)}
                  />
                  <VStack gap={0}>
                    <Text weight="medium">{variant.posthogVariantKey}</Text>
                    <Text size="sm" color="tertiary">
                      Will be deleted
                    </Text>
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}
        </VStack>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSync}
            disabled={loading || syncing || totalSelected === 0}
          >
            {syncing
              ? 'Syncing...'
              : `Sync ${totalSelected} Change${totalSelected !== 1 ? 's' : ''}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
